// @flow
import invariant from 'invariant';
import { sprintf } from 'sprintf-js';
import interpolateComponents from 'interpolate-components';
import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import ViewportContext from 'containers/ViewportContext';
import { H5 } from 'components/Text';
import Box from 'components/Box';
import TrackedLink from 'components/TrackedLink';
import Button from 'components/Button';
import Icon from 'components/Icon';
import { configValue } from 'utils/config';
import { gutterWidth, transition } from '../theme';

const { useRef, useContext, useState, useEffect } = React;

const FlashLink = styled(TrackedLink)`
  font-weight: inherit;
`;

const flashMessageTypeMap = {
  SAVED_SEARCH: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'You are now subscribed to an email alert for this search!'
    ),
    style: 'success'
  }),
  UNSAVED_SEARCH: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'You have removed your email alert.'
    ),
    style: 'success'
  }),
  NO_QUEUE_ITEMS: () => ({
    message: /* TRANSLATORS: staff reviewed all orgs */ getText(
      'There are no more queue items to review.'
    ),
    style: 'info'
  }),
  USER_ACCOUNT_DELETED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your account has been deleted.'
    ),
    style: 'success'
  }),
  PAYMENT_SUCCESSFUL: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Thanks! Your payment was successful.'
    ),
    style: 'success'
  }),
  PAYMENT_PENDING_CORP: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Thanks! Your payment is now pending. ' +
        'It will be processed when your job listing is approved.'
    ),
    style: 'success'
  }),
  PAYMENT_PENDING_ORG: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Thanks! Your payment is now pending. ' +
        'It will be processed when your organization is approved.'
    ),
    style: 'success'
  }),
  PAYMENT_METHOD_ADDED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Payment method added.'
    ),
    style: 'success'
  }),
  PAYMENT_METHOD_REMOVED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Payment method removed.'
    ),
    style: 'success'
  }),
  JOB_FAMILY_SAVED: () => ({
    message: getText('Job family saved'),
    style: 'success'
  }),
  JOB_FAMILY_DELETED: () => ({
    message: getText('Job family deleted'),
    style: 'success'
  }),
  JOB_TITLE_SAVED: () => ({
    message: getText('Job title saved'),
    style: 'success'
  }),
  JOB_TITLE_DELETED: () => ({
    message: getText('Job title deleted'),
    style: 'success'
  }),
  JOB_SYNONYM_SAVED: () => ({
    message: getText('Job synonym saved'),
    style: 'success'
  }),
  JOB_SYNONYM_DELETED: () => ({
    message: getText('Job synonym deleted'),
    style: 'success'
  }),
  PAGE_SAVED: () => ({
    message: getText('Page saved'),
    style: 'success'
  }),
  PAGE_DELETED: () => ({
    message: getText('Page deleted'),
    style: 'success'
  }),
  CATEGORY_SAVED: () => ({
    message: getText('Category saved'),
    style: 'success'
  }),
  CATEGORY_DELETED: () => ({
    message: getText('Category deleted'),
    style: 'success'
  }),
  TAG_SAVED: () => ({
    message: getText('Tag saved'),
    style: 'success'
  }),
  TAG_DELETED: () => ({
    message: getText('Tag deleted'),
    style: 'success'
  }),
  SIDEBAR_SAVED: () => ({
    message: getText('Sidebar saved'),
    style: 'success'
  }),
  SUBSITE_SAVED: () => ({
    message: getText('Subsite saved'),
    style: 'success'
  }),
  SUBSITE_DELETED: () => ({
    message: getText('Subsite deleted'),
    style: 'success'
  }),
  SCHEDULED_CONTENT_QUEUE_SAVED: () => ({
    message: getText('Queue saved'),
    style: 'success'
  }),
  SCHEDULED_CONTENT_QUEUE_DELETED: () => ({
    message: getText('Queue deleted'),
    style: 'success'
  }),
  SCHEDULED_CONTENT_SAVED: () => ({
    message: getText('Scheduled content saved'),
    style: 'success'
  }),
  SCHEDULED_CONTENT_DELETED: () => ({
    message: getText('Scheduled content deleted'),
    style: 'success'
  }),
  REDIRECT_SAVED: () => ({
    message: getText('Redirect saved'),
    style: 'success'
  }),
  REDIRECT_DELETED: () => ({
    message: getText('Redirect deleted'),
    style: 'success'
  }),
  CMS_GENERIC_SAVED: () => ({
    message: getText('Saved'),
    style: 'success'
  }),
  USER_SETTINGS_SAVED: () => ({
    message: /* TRANSLATORS: confirmation message, personal settings */ getText(
      'Settings saved.'
    ),
    style: 'success'
  }),
  EMAIL_VERIFIED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your email address has been verified.'
    ),
    style: 'success'
  }),
  FORBIDDEN: () => ({
    message: /* TRANSLATORS: flash message when attempting to view a page you don't have access to */ getText(
      'Forbidden'
    ),
    style: 'error'
  }),
  LOGIN_REQUIRED: () => ({
    message: /* TRANSLATORS: flash message when redirected to login */ getText(
      'Please log in first.'
    ),
    style: 'warn'
  }),
  LOGGED_OUT: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'You have been logged out.'
    ),
    style: 'success'
  }),
  UNSUBSCRIBE_ALERT: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Email alert cancelled'
    ),
    style: 'success'
  }),
  OAUTH_LINKED: data => {
    let type;
    if (!data) {
      type = '';
    } else {
      type = data.type;
    }
    return {
      message: sprintf(
        /* TRANSLATORS: confirmation message, linked Facebook/Google */ getText(
          'Your %(providerType)s account is now linked.'
        ),
        { providerType: type }
      ),
      style: 'success'
    };
  },
  OAUTH_UNLINKED: data => {
    let type;
    if (!data) {
      type = '';
    } else {
      type = data.type;
    }
    return {
      message: sprintf(
        /* TRANSLATORS: confirmation message, linked Facebook/Google */ getText(
          'Your %(providerType)s account has been unlinked.'
        ),
        { providerType: type }
      ),
      style: 'success'
    };
  },
  PASSWORD_CHANGED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your password has been changed.'
    ),
    style: 'success'
  }),
  LISTING_SAVED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your listing has been saved.'
    ),
    style: 'success'
  }),
  LISTING_SAVED_PUBLISHED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your listing has been saved and published.'
    ),
    style: 'success'
  }),
  LISTING_DRAFT_SAVED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      "Listing saved! Don't forget to publish it when you're ready."
    ),
    style: 'success'
  }),
  LISTING_PUBLISHED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your listing has been published.'
    ),
    style: 'success'
  }),
  LISTING_REPOSTED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your listing has been reposted.'
    ),
    style: 'success'
  }),
  LISTING_DELETED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your listing has been deleted.'
    ),
    style: 'success'
  }),
  LISTING_HIDDEN: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your listing has been hidden.'
    ),
    style: 'success'
  }),
  LISTING_UNHIDDEN: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your listing has been unhidden.'
    ),
    style: 'success'
  }),
  ORG_PROFILE_SAVED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your changes have been saved!'
    ),
    style: 'success'
  }),
  ORG_DELETED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your organization has been deleted. Good luck!'
    ),
    style: 'success'
  }),
  ADMIN_REQUEST_SENT_TO_STAFF: () => ({
    message: /* TRANSLATORS: admin request confirmation message */ getText(
      'Your request has been sent to the Idealist support staff.'
    ),
    style: 'success'
  }),
  ADMIN_REQUEST_SENT: data => {
    invariant(data && data.name, 'missing name');
    return {
      message: sprintf(
        /* TRANSLATORS: admin request confirmation message */ getText(
          'Your request has been sent to the administrators of %(name)s.'
        ),
        { name: data.name }
      ),
      style: 'success'
    };
  },
  ADMIN_REQUEST_APPROVED: data => {
    invariant(data && data.name, 'missing name');
    return {
      message: sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          '%(name)s is now an administrator!'
        ),
        { name: data.name }
      ),
      style: 'success'
    };
  },
  ADMIN_REQUEST_REJECTED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'You have denied this administrator request.'
    ),
    style: 'success'
  }),
  INVALID_ADMIN_REQUEST: () => ({
    /* TRANSLATORS: confirmation message */
    message: getText(
      'The link you followed is either expired or has already been used.'
    ),
    style: 'warn'
  }),
  ADMIN_ADDED: data => {
    let message = '';
    if (data) {
      message = sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          'Thanks! %(name)s is now an administrator!'
        ),
        { name: data.name }
      );
    }
    return {
      message,
      style: 'success'
    };
  },
  ADMIN_INVITED: data => {
    let message = '';
    if (data) {
      message = sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          "Thanks! We've sent an invite to %(email)s."
        ),
        { email: data.email }
      );
    }
    return {
      message,
      style: 'success'
    };
  },
  OWNER_ADDED: data => {
    let message = '';
    if (data) {
      message = sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          'Thanks! %(name)s is now an owner!'
        ),
        { name: data.name }
      );
    }
    return {
      message,
      style: 'success'
    };
  },
  OWNER_INVITED: data => {
    let message = '';
    if (data) {
      message = sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          "Thanks! We've sent an invite to %(email)s."
        ),
        { email: data.email }
      );
    }
    return {
      message,
      style: 'success'
    };
  },
  OWNER_PROMOTED: data => {
    let message = '';
    if (data) {
      message = sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          "You've just made %(name)s an owner!"
        ),
        { name: data.name }
      );
    }
    return {
      message,
      style: 'success'
    };
  },
  REMOVED_OWNER: data => {
    let message = '';
    if (data) {
      message = sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          '%(userName)s is no longer an owner for %(orgName)s.'
        ),
        { userName: data.userName, orgName: data.orgName }
      );
    }
    return {
      message,
      style: 'success'
    };
  },
  REMOVED_ADMIN: data => {
    let message = '';
    if (data) {
      message = sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          '%(userName)s is no longer an administrator for %(orgName)s.'
        ),
        { userName: data.userName, orgName: data.orgName }
      );
    }
    return {
      message,
      style: 'success'
    };
  },
  REMOVED_OWNER_SELF: data => {
    let message = '';
    if (data) {
      message = sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          "You've removed yourself as an owner for %(orgName)s."
        ),
        { orgName: data.orgName }
      );
    }
    return {
      message,
      style: 'success'
    };
  },
  REMOVED_ADMIN_SELF: data => {
    let message = '';
    if (data) {
      message = sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          "You've removed yourself as an administrator for %(orgName)s."
        ),
        { orgName: data.orgName }
      );
    }
    return {
      message,
      style: 'success'
    };
  },
  REMOVED_GROUP_OWNER_SELF: data => {
    let message = '';
    if (data) {
      message = sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          "You've removed yourself as an owner for %(groupName)s."
        ),
        { groupName: data.groupName }
      );
    }
    return {
      message,
      style: 'success'
    };
  },
  REMOVED_GROUP_ADMIN_SELF: data => {
    let message = '';
    if (data) {
      message = sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          "You've removed yourself as an administrator for %(groupName)s."
        ),
        { groupName: data.groupName }
      );
    }
    return {
      message,
      style: 'success'
    };
  },
  REMOVED_GROUP_OWNER: data => {
    let message = '';
    if (data) {
      message = sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          '%(userName)s is no longer an owner for %(groupName)s.'
        ),
        { userName: data.userName, groupName: data.groupName }
      );
    }
    return {
      message,
      style: 'success'
    };
  },
  REMOVED_GROUP_ADMIN: data => {
    let message = '';
    if (data) {
      message = sprintf(
        /* TRANSLATORS: confirmation message */ getText(
          '%(userName)s is no longer an administrator for %(groupName)s.'
        ),
        { userName: data.userName, groupName: data.groupName }
      );
    }
    return {
      message,
      style: 'success'
    };
  },
  SAVE_ORG: data => {
    invariant(data && data.orgName, 'Missing orgName');
    const message = sprintf(
      /* TRANSLATORS: confirmation message */ getText(
        '%(orgName)s has been saved. You will now receive notifications' +
          ' whenever %(orgName)s posts a listing!'
      ),
      { orgName: data.orgName }
    );
    return {
      message,
      style: 'success'
    };
  },
  UNSAVE_ORG: data => {
    invariant(data && data.orgName, 'Missing orgName');
    const message = sprintf(
      /* TRANSLATORS: confirmation message */ getText(
        'You have unsaved %(orgName)s. You will no longer receive' +
          ' notifications from %(orgName)s.'
      ),
      { orgName: data.orgName }
    );
    return {
      message,
      style: 'success'
    };
  },
  SAVE_GROUP: data => {
    invariant(data && data.groupName, 'Missing groupName');
    const message = sprintf(
      /* TRANSLATORS: confirmation message */ getText(
        '%(groupName)s has been saved. You will now receive notifications' +
          ' whenever %(groupName)s posts an event!'
      ),
      { groupName: data.groupName }
    );
    return {
      message,
      style: 'success'
    };
  },
  UNSAVE_GROUP: data => {
    invariant(data && data.groupName, 'Missing groupName');
    const message = sprintf(
      /* TRANSLATORS: confirmation message */ getText(
        'You have unsaved %(groupName)s. You will no longer receive' +
          ' notifications from %(groupName)s.'
      ),
      { groupName: data.groupName }
    );
    return {
      message,
      style: 'success'
    };
  },
  GROUP_PROFILE_SAVED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your changes have been saved!'
    ),
    style: 'success'
  }),
  GROUP_DELETED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your group has been deleted. Good luck!'
    ),
    style: 'success'
  }),
  EVENT_DRAFT_SAVED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      "Event saved! Don't forget to publish it when you're ready."
    ),
    style: 'success'
  }),
  EVENT_SAVED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your event has been saved.'
    ),
    style: 'success'
  }),
  EVENT_PUBLISHED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your event has been published.'
    ),
    style: 'success'
  }),
  EVENT_PENDING_APPROVAL: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Thank you! Your event is now awaiting approval.'
    ),
    style: 'success'
  }),
  EVENT_REPOSTED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your event has been reposted.'
    ),
    style: 'success'
  }),
  EVENT_SAVED_PUBLISHED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your event has been saved and published.'
    ),
    style: 'success'
  }),
  SAVED_NOTIFICATION_PREFERENCES: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your notification preferences have been saved.'
    ),
    style: 'success'
  }),
  GRAD_PROGRAM_SAVED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your graduate program has been saved.'
    ),
    style: 'success'
  }),
  GRAD_PROGRAM_PENDING_SAVED: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      'Your graduate program has been saved but requires completing the checkout process to be published.'
    ),
    style: 'success'
  }),
  LISTING_FLAGGED: () => ({
    message: getText('Thanks for reporting this!'),
    style: 'success'
  }),
  PROMO_CREDIT_GIVEN: () => ({
    message: getText('Promo credit given.'),
    style: 'success'
  }),
  ORG_WALKTHROUGH_WEBINAR: data => {
    const linkClickHandler = data.linkClickHandler || (() => undefined);
    return {
      message: interpolateComponents({
        mixedString: /* TRANSLATORS: Idealist Walkthrough Webinars banner on Org Dashboard */ getText(
          'Get the most from our new site! Find out how {{link}}here{{/link}}.'
        ),
        components: {
          link: (
            <FlashLink
              href="https://pi.idealist.org/officehours-wi2018-19"
              eventName="Idealist Walkthrough Webinars"
              onClick={linkClickHandler}
            />
          )
        }
      }),
      style: 'info'
    };
  },
  STAFF_PASSWORD_CHANGED: () => ({
    message: getText('Password changed.'),
    style: 'success'
  }),
  SUBSCRIBED_SUBSITE: () => ({
    message: getText('Subscribed!'),
    style: 'success'
  }),
  HERO_SEARCH_DELETED: () => ({
    message: getText('Search hero deleted'),
    style: 'success'
  }),
  SURVEY_SUBMITTED_TOO_MANY_TIMES: () => ({
    message: /* TRANSLATORS: confirmation message */ getText(
      `We already have your survey information, but we love your enthusiasm!`
    ),
    style: 'success'
  }),
  LISTING_APPLICATION_SUBMITTED: () => ({
    message: /* TRANSLATORS: application confirmation message */ getText(
      'Application submitted successfully.'
    ),
    style: 'success'
  })
};

const styleColor = {
  success: { bg: 'turtleGreen', color: 'white' },
  info: { bg: 'battleshipGrey', color: 'white' },
  warn: { bg: 'dandelion', color: 'charcoalGrey' },
  error: { bg: 'scarlet', color: 'white' }
};

type Props = {|
  flashMessage: FlashMessageType,
  removeMessage: () => void
|};

const FlashMessage = ({
  flashMessage,
  removeMessage,
  ...props
}: {
  ...Props
}) => {
  const { message, style } = flashMessageTypeMap[flashMessage.type](
    flashMessage.data
  );
  const flashTheme = styleColor[style] || styleColor.info;
  const closeHandler = () => {
    removeMessage();
    if (flashMessage.onRemove) {
      flashMessage.onRemove();
    }
  };

  return (
    <Box
      data-qa-id="flash-message"
      display="flex"
      position="relative"
      alignItems="center"
      justifyContent="center"
      px={gutterWidth / 2 + 8}
      py="10px"
      bg={flashTheme.bg}
      color={flashTheme.color}
      {...props}
    >
      <H5 my={0} px={[gutterWidth / 2, null, null, 0]}>
        {message}
      </H5>
      <Box
        position="absolute"
        right={gutterWidth / 2}
        top="50%"
        transform="translateY(-50%)"
      >
        <Button
          data-qa-id="flash-message-close"
          onClick={closeHandler}
          aria-label={getText('Close alert message')}
        >
          <Icon name="close" width={13} height={12} fill="currentColor" />
        </Button>
      </Box>
    </Box>
  );
};

const aDisappear = height => keyframes`
  from {
    opacity: 1;
    ${height !== 'auto' &&
      height > 0 &&
      `
      height: ${height}px;
    `}
    pointer-events: auto;
  }
  to {
    opacity: 0;
    height: 0;
    pointer-events: none;
  }
`;

const DisappearingContainer = styled.div`${props =>
  props.height !== 'auto' &&
  props.height > 0 &&
  `
  overflow: hidden;
  height: ${props.height}px;
`}
  animation: ${transition} ${props =>
  `${props.transitionTime || 5}`}s forwards ${aDisappear};
  will-change: height;
`;

const DisappearingFlashMessage = (props: { ...Props }) => {
  const [height, setHeight] = useState('auto');
  const viewportWidth = useContext(ViewportContext)[0];
  const containerRef = useRef(null);
  const transitionTime = configValue('idealist', 'flashMessageDuration');
  useEffect(
    () => {
      if (containerRef && containerRef.current) {
        const newHeight = containerRef.current.offsetHeight;
        setHeight(newHeight || 'auto');
      }
    },
    [viewportWidth]
  );

  return (
    <DisappearingContainer
      ref={containerRef}
      height={height}
      transitionTime={transitionTime}
    >
      <FlashMessage {...props} />
    </DisappearingContainer>
  );
};

export default DisappearingFlashMessage;
